/**
 * Remove a device from You › Devices (docs/security.md "Session devices", docs/e2e.md "Trusted
 * browsers follow rotations").
 *
 * - A **"Just this session"** browser (proved by its passkey-signed endorsement) was never a key-directory recipient and kept its keys in
 *   memory only, so removing it needs no new epoch: the hub ends its sign-in and sockets, and
 *   pushes the end to every daemon (`trust.sync` `endedSessions`), which refuse it at once. No
 *   passkey tap, and no other browser is left behind on an old key.
 * - A **trusted** browser or machine gets the passkey-signed revocation list and an immediate
 *   rotation to an independent epoch that leaves it out, then the hub-local block.
 */
import type { Api } from './api.js'
import type { BrowserContentSession } from './content-session.js'
import type { Device } from './types.js'

export async function removeDevice(
  device: Pick<Device, 'id' | 'session' | 'endorsement'>,
  deps: {
    content?: Pick<BrowserContentSession, 'revoke' | 'verifiedSessionDevice'> | undefined
    api: Pick<Api, 'revokeDevice'>
  },
): Promise<{ rotated: boolean }> {
  // The relay's `session` flag alone never skips a rotation: its passkey-signed endorsement must
  // verify as a session endorsement against this browser's pinned passkeys.
  if (
    device.session &&
    (!deps.content || (await deps.content.verifiedSessionDevice(device.id, device.endorsement)))
  ) {
    await deps.api.revokeDevice(device.id)
    return { rotated: false }
  }
  // Signed revocation ends its sessions and rotates keys it can no longer read.
  if (deps.content) await deps.content.revoke({ devices: [device.id] })
  await deps.api.revokeDevice(device.id)
  return { rotated: !!deps.content }
}
